/**
 * Account Delete Confirmation Modal Component
 * Modal for confirming account deletion
 * Task 003a - Remove Allocated & Remaining Balance
 * Task 003c - Account Closing & Deletion Flow
 */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAccounts } from '../contexts/AccountContext';

interface AccountDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  accountName: string;
  accountId: string;
  isDeleting?: boolean;
}

export const AccountDeleteModal: React.FC<AccountDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  accountName,
  accountId,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  const { getTransactionCount } = useAccounts();
  const [hasTransactions, setHasTransactions] = useState<boolean>(false);
  const [transactionCount, setTransactionCount] = useState<number>(0);
  const [isChecking, setIsChecking] = useState<boolean>(true);

  useEffect(() => {
    if (isOpen && accountId) {
      setIsChecking(true);
      getTransactionCount(accountId)
        .then((result) => {
          setHasTransactions(result.has_transactions);
          setTransactionCount(result.transaction_count);
        })
        .catch((err) => {
          console.error('Failed to check transactions:', err);
          setHasTransactions(false);
          setTransactionCount(0);
        })
        .finally(() => {
          setIsChecking(false);
        });
    }
  }, [isOpen, accountId, getTransactionCount]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-red-600 border-b-4 border-red-800 p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
          >
            {t('account_delete_modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-red-800"
            style={{ border: '2px solid #991b1b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
            disabled={isDeleting || isChecking}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {isChecking ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : hasTransactions ? (
            /* Has Transactions - Show Warning */
            <>
              <p className="font-mono text-sm text-[#1a1a1a]">
                {t('account_delete_modal.has_transactions', { count: transactionCount, name: accountName })}
              </p>
            </>
          ) : (
            /* No Transactions - Normal Delete */
            <>
              <p className="font-mono text-sm text-[#1a1a1a]">
                {t('account_delete_modal.confirm_message', { name: accountName })}
              </p>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '4px 4px 0 #077f5d'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
            disabled={isDeleting || isChecking}
          >
            {hasTransactions ? t('account_delete_modal.understood') : t('account_delete_modal.cancel')}
          </button>
          {!hasTransactions && !isChecking && (
            <button
              type="button"
              onClick={onConfirm}
              className="flex-[2] px-4 py-3 bg-red-600 text-white border-2 border-red-800 font-bold uppercase disabled:opacity-50"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '10px',
                boxShadow: '6px 6px 0 #991b1b'
              }}
              onMouseEnter={(e) => {
                if (!isDeleting) {
                  e.currentTarget.style.boxShadow = '4px 4px 0 #991b1b';
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '6px 6px 0 #991b1b';
                e.currentTarget.style.transform = 'translate(0, 0)';
              }}
              disabled={isDeleting}
            >
              {isDeleting ? t('account_delete_modal.deleting') : t('account_delete_modal.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
